import NotificationDialog from 'dialogs/NotificationDialog/NotificationDialog'
import { isInProvingRole } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { useDialogState } from 'hooks/useDialogState'
import { ProvingType, ProvingUserRole, UserRole } from 'model'
import { IPivotProps, Pivot, PivotItem } from 'office-ui-fabric-react'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageGeneralStatus } from './PageGeneral'

const pagesAll = [
  'general',
  'participants',
  'vehicles',
  'materials',
  'resourceRequirements',
  'resourceBookings',
  'forms',
  'workflow',
  'files',
  'mails',
  'specialNote',
  'editingHistory',
]

const pagesIsExternal = ['general', 'participants', 'mails', 'files']
const pagesIsReader = [
  'general',
  'participants',
  'vehicles',
  'materials',
  'resourceRequirements',
  'resourceBookings',
  'forms',
  'workflow',
  'files',
  'specialNote',
]

// Bei Erprobungstyp 'Dienstreise' werden keine Fahrzeuge oder Materialien erfasst
const pagesBusinessTrip = [
  'general',
  'participants',
  'resourceRequirements',
  'resourceBookings',
  'forms',
  'workflow',
  'files',
  'mails',
  'specialNote',
]
const pagesIsReaderBusinessTrip = [
  'general',
  'participants',
  'resourceRequirements',
  'resourceBookings',
  'forms',
  'workflow',
  'files',
  'specialNote',
]

export type ProvingPaginationProps = Omit<
  IPivotProps,
  'selectedKey' | 'onLinkClick'
> & {
  selectedPage: string
  onSelectPage: (page: string) => void
  generalIsValid: PageGeneralStatus
}
enum ErrorMessages {
  INVALID = 'Bitte beheben Sie die Fehler im Formular und speichern bevor Sie fortfahren.',
  DIRTY = 'Bitte speichern Sie bevor Sie fortfahren.',
}

export const ProvingPagination: React.FC<ProvingPaginationProps> = ({
  selectedPage,
  onSelectPage,
  generalIsValid,
  ...props
}) => {
  const { t } = useTranslation(['ProvingDetailsView', 'common'])
  const [proving] = useCurrentProving()
  const auth = useAuthContext()
  const isExternal = auth.hasUserRole(UserRole.Hrg)
  const isReader = auth.hasUserRole(UserRole.Reader)
  const isMaintenanceHead = isInProvingRole(
    proving,
    ProvingUserRole.MaintenanceHead
  )
  const isProvingManager = isInProvingRole(
    proving,
    ProvingUserRole.ProvingManager
  )
  const [dialogText, setDialogText] = useState<ErrorMessages>()
  const [errorsDialogState, showErrorsDialog, ,] = useDialogState()
  const handleLinkClicked = React.useCallback(
    (link?: PivotItem) => {
      if (link) {
        if (link.props.itemKey === 'general') {
          onSelectPage(link.props.itemKey!)
        } else {
          switch (generalIsValid) {
            case PageGeneralStatus.CORRECT:
              onSelectPage(link.props.itemKey!)
              break
            case PageGeneralStatus.DIRTY:
              setDialogText(ErrorMessages.DIRTY)
              showErrorsDialog()
              break
            case PageGeneralStatus.INVALID:
              setDialogText(ErrorMessages.INVALID)
              showErrorsDialog()
              break
            default:
              break
          }
        }
      }
    },
    [onSelectPage, generalIsValid, showErrorsDialog]
  )

  let pages =
    proving.type === ProvingType.BusinessTrip ? pagesBusinessTrip : pagesAll

  if (isExternal) {
    pages = pagesIsExternal
  }
  if (isReader || isMaintenanceHead || isProvingManager) {
    if (proving.type === ProvingType.BusinessTrip) {
      pages = pagesIsReaderBusinessTrip
    } else {
      if (proving.status === 9) {
        pages = pagesIsReader.filter((v) => v !== 'vehicles')
      } else {
        pages = pagesIsReader
      }
    }
  }

  return (
    <>
      <Pivot
        {...props}
        headersOnly={true}
        selectedKey={selectedPage}
        onLinkClick={handleLinkClicked}
      >
        {pages.map((page) => (
          <PivotItem
            key={page}
            itemKey={page}
            headerText={t(`pages.${page}.title`)}
          />
        ))}
      </Pivot>
      <NotificationDialog {...errorsDialogState} titleText={dialogText} />
    </>
  )
}
